// Use local date parts (not toISOString, which is UTC and can land on the
// wrong calendar day depending on the user's timezone/time of day).
export const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatTimestamp = (date) => {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${toDateInputValue(date)} ${hours}:${minutes}`;
};

// UTC offset (e.g. "+03:30", "-05:00") of `timeZone` at the instant `date`
// falls on — JS Date has no notion of an arbitrary IANA zone, so this reads
// the wall-clock time Intl would render in that zone and diffs it against
// the same instant in UTC.
const timezoneOffsetSuffix = (date, timeZone) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value])
  );
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  const offsetMinutes = Math.round((asUtc - date.getTime()) / 60000);
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
};

// Turns the plain Y-m-d boundaries from the date-range picker into
// timezone-anchored ISO instants, so "today" in the selected timezone maps
// to the right UTC window server-side regardless of the browser's own zone.
// Backend just does DateTime.tryParse on these (see backoffice.dart), which
// already honors an explicit offset, so no backend change is needed.
export const toDateRangeParams = (dateFrom, dateTo, timeZone) => ({
  dateFrom: dateFrom
    ? `${dateFrom}T00:00:00.000${timezoneOffsetSuffix(new Date(`${dateFrom}T00:00:00`), timeZone)}`
    : undefined,
  dateTo: dateTo
    ? `${dateTo}T23:59:59.999${timezoneOffsetSuffix(new Date(`${dateTo}T23:59:59`), timeZone)}`
    : undefined,
});

// Money formatting, "$" prefix (sign goes before the "$", e.g. "-$40", not
// "$-40"). [decimals] is the exact number of decimals shown (not a max) —
// callers that want up-to-2-decimals trimming of trailing zeros use
// formatMoneyFlexible instead.
export const formatMoneyFixed = (value, decimals) => {
  const num = Number(value || 0);
  return `${num < 0 ? "-" : ""}$${Math.abs(num).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};

// Table cells / axis ticks: up to 2 decimals, trailing zeros trimmed.
export const formatMoneyFlexible = (value) => {
  const num = Number(value || 0);
  return `${num < 0 ? "-" : ""}$${Math.abs(num).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

export const formatAmount = formatMoneyFlexible;

export const toggleInList = (list, value) => {
  const idx = list.indexOf(value);
  if (idx >= 0) {
    list.splice(idx, 1);
  } else {
    list.push(value);
  }
};
